import { getMyotomeMappingForMuscle } from '../data/emgClinicalCriteria';
import type { Patient } from '../types/patient';
import type { NCSTestReferenceValues, NCSTestResult } from '../types/ncs';
import type { ClinicalReport, EMGMuscleResult } from './jsonReportGenerator';
import { buildClinicalImpression, type DiagnosticAnalysisResult } from './clinicalImpressionBuilder';

const line = '-'.repeat(72);

function valueOrDash(value: unknown, suffix = ''): string {
  if (value === undefined || value === null || value === '') return '-';
  return `${value}${suffix}`;
}

function formatReferenceValues(referenceValues?: NCSTestReferenceValues): string {
  if (!referenceValues) return 'Sin referencia documentada';
  const parts = [
    referenceValues.latency?.max !== undefined ? `Lat <= ${referenceValues.latency.max} ms` : '',
    referenceValues.amplitude?.min !== undefined ? `Amp >= ${referenceValues.amplitude.min}` : '',
    referenceValues.velocity?.min !== undefined ? `VC >= ${referenceValues.velocity.min} m/s` : '',
    referenceValues.duration?.max !== undefined ? `Dur <= ${referenceValues.duration.max} ms` : '',
    referenceValues.fWaveLatency?.max !== undefined ? `F <= ${referenceValues.fWaveLatency.max} ms` : '',
  ].filter(Boolean);
  return parts.length > 0 ? parts.join('; ') : 'Sin referencia documentada';
}

function formatNcsRow(result: NCSTestResult): string {
  return [
    `${result.nerve} (${result.side}, ${result.type})`,
    `Estim: ${valueOrDash(result.stimulationSite || result.site)}`,
    `Reg: ${valueOrDash(result.recordingSite)}`,
    `Dist: ${valueOrDash(result.distance, result.distanceUnit || ' cm')}`,
    `Lat: ${valueOrDash(result.latency, result.latencyUnit || ' ms')}`,
    `Pico: ${valueOrDash(result.peakLatency, ' ms')}`,
    `Amp: ${valueOrDash(result.amplitude, result.amplitudeUnit || '')}`,
    `VC: ${valueOrDash(result.velocity, ' m/s')}`,
    `Temp: ${valueOrDash(result.temperature, ' °C')}`,
    `Ref: ${formatReferenceValues(result.referenceValues)}`,
    `Estado: ${result.status}`,
  ].join(' | ');
}

function formatEmgRow(muscle: EMGMuscleResult): string {
  const mapping = getMyotomeMappingForMuscle(muscle.muscleName);
  const roots = mapping?.roots.length ? mapping.roots.join('/') : '-';
  const nerve = mapping?.peripheralNerve || '-';
  return [
    `${muscle.muscleName} (${muscle.side})`,
    `Raíces: ${roots}`,
    `Nervio: ${nerve}`,
    `Insercional: ${muscle.insertionalActivity}`,
    `Espontánea: ${muscle.spontaneousActivitySummary}`,
    `PUM: amp ${muscle.mupAmplitude} µV, dur ${muscle.mupDuration} ms, polif ${muscle.mupPolyphasia}%`,
    `Reclutamiento: ${muscle.recruitmentPattern}`,
    muscle.interpretationNotes ? `Notas: ${muscle.interpretationNotes}` : '',
  ].filter(Boolean).join(' | ');
}

export function buildPlainTextEdxReport(
  report: ClinicalReport,
  patient: Patient,
  analysis?: DiagnosticAnalysisResult
): string {
  const impression = buildClinicalImpression(report, analysis);
  const sections: string[] = [];

  sections.push(
    `REPORTE DE ELECTRONEUROMIOGRAFÍA`,
    `Fecha de generación: ${new Date(report.timestamp).toLocaleString()}`,
    `Versión del reporte: ${report.version}`,
    `Reporte ID: ${report.reportId}`,
    line,
    `PACIENTE`,
    `ID: ${report.patient.id}`,
    `Nombre: ${patient.firstName} ${patient.lastName}`,
    `Edad: ${report.patient.demographics.age} años`,
    `Sexo: ${report.patient.demographics.sex}`,
    `Indicación / pregunta clínica: ${impression.indication}`,
    line,
    `CONDICIONES TÉCNICAS Y SEGURIDAD`,
    `Laboratorio: ${valueOrDash(report.metadata.laboratoryName)}`,
    `Fecha/hora del estudio: ${new Date(report.metadata.studyDateTime).toLocaleString()}`,
    `Médico intérprete: ${valueOrDash(report.metadata.interpretingPhysician)}`,
    `Profesional que realizó el estudio: ${valueOrDash(report.metadata.performingClinician)}`,
    `Equipo: ${[
      report.metadata.equipment?.manufacturer,
      report.metadata.equipment?.model,
      report.metadata.equipment?.serialNumber,
    ].filter(Boolean).join(' ') || '-'}`,
    `Electrodo de aguja: ${report.metadata.needleElectrode || 'not_specified'}`,
    `Marcapasos/dispositivo implantable: ${report.metadata.safetyScreening.pacemakerOrImplantableDevice ? 'Sí' : 'No'}`,
    `Anticoagulación/riesgo hemorrágico: ${report.metadata.safetyScreening.anticoagulation ? 'Sí' : 'No'}`,
    `Limitaciones: ${impression.limitations}`,
    report.metadata.intervalComparison?.summary
      ? `Cambio de intervalo: ${report.metadata.intervalComparison.summary}`
      : `Cambio de intervalo: No documentado`,
    line,
  );

  sections.push(`IMPRESIÓN DIAGNÓSTICA`, impression.copySummary, line);

  if (report.electrophysiologicalData.ncs?.results.length) {
    sections.push(`TABLA DE NEUROCONDUCCIONES (NCS)`);
    report.electrophysiologicalData.ncs.results.forEach(result => sections.push(formatNcsRow(result)));
    sections.push(line);
  }

  const emg = report.electrophysiologicalData.emg;
  if (emg) {
    sections.push(`TABLA DE ELECTROMIOGRAFÍA CON AGUJA (EMG)`);
    if (emg.status === 'performed' && emg.muscles.length > 0) {
      emg.muscles.forEach(muscle => sections.push(formatEmgRow(muscle)));
    } else {
      sections.push(emg.status === 'skipped' ? 'EMG no realizada.' : 'Sin datos EMG interpretables.');
    }
    sections.push(line);
  }

  const specialStudies = report.electrophysiologicalData.specialStudies;
  if (specialStudies && !specialStudies.notPerformed && specialStudies.tests.length > 0) {
    sections.push(`ESTUDIOS ESPECIALES`);
    specialStudies.tests.forEach(test => {
      const values = Object.entries(test.values || {})
        .map(([key, value]) => `${key}: ${value}${test.unit || ''}`)
        .join(', ');
      sections.push(`${test.name} (${test.side}, ${test.type}) | ${values || '-'} | Estado: ${test.status}`);
      if (test.findings?.length) sections.push(`  Hallazgos: ${test.findings.join('. ')}`);
    });
    sections.push(line);
  }

  return sections.join('\n');
}
